import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  ChevronLeft,
  ExpandLess,
  ExpandMore,
  Notifications,
  Settings,
  Logout,
  Person,
  Home,
  NavigateNext,
} from '@mui/icons-material';
import { brandColors, designTokens } from '~/presentation/themes/modernTheme';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

const StyledAppBar = styled(AppBar)(({ theme, open, drawerWidth }) => ({
  backgroundColor: '#ffffff',
  color: brandColors.neutral[800],
  boxShadow: `0 1px 3px ${brandColors.neutral[200]}`,
  borderBottom: `1px solid ${brandColors.neutral[200]}`,
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const StyledDrawer = styled(Drawer)(({ theme, open, drawerWidth, collapsedWidth }) => ({
  width: open ? drawerWidth : collapsedWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : collapsedWidth,
    backgroundColor: brandColors.neutral[900],
    color: '#ffffff',
    borderRight: 'none',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: brandColors.neutral[700],
      borderRadius: 3,
    },
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  minHeight: 64,
  borderBottom: `1px solid ${brandColors.neutral[700]}`,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active, level = 0 }) => ({
  margin: `${designTokens.spacing.xs}px ${designTokens.spacing.sm}px`,
  borderRadius: designTokens.borderRadius.md,
  paddingLeft: theme.spacing(2 + level * 2),
  minHeight: 48,
  color: active ? '#ffffff' : brandColors.neutral[300],
  backgroundColor: active ? brandColors.primary[600] : 'transparent',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    backgroundColor: active ? brandColors.primary[700] : brandColors.neutral[800],
    color: '#ffffff',
  },
  
  '& .MuiListItemIcon-root': {
    color: 'inherit',
    minWidth: 40,
  },
  
  '& .MuiListItemText-primary': {
    fontFamily: designTokens.typography.fontFamily,
    fontWeight: active ? designTokens.typography.fontWeights.semibold : designTokens.typography.fontWeights.medium,
    fontSize: '0.875rem',
  },
}));

const MainContent = styled(Box)(({ theme, open, drawerWidth, collapsedWidth }) => ({
  flexGrow: 1,
  padding: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: open ? drawerWidth : collapsedWidth,
  minHeight: '100vh',
  backgroundColor: brandColors.neutral[25],
}));

const ContentArea = styled(Box)(({ theme }) => ({
  padding: designTokens.spacing.xl,
  minHeight: 'calc(100vh - 64px)',
}));

const BreadcrumbContainer = styled(Box)(({ theme }) => ({
  padding: `${designTokens.spacing.md}px ${designTokens.spacing.xl}px`,
  backgroundColor: '#ffffff',
  borderBottom: `1px solid ${brandColors.neutral[200]}`,
}));

const ModernLayout = ({
  children,
  menuItems = [],
  user,
  onLogout,
  onProfileClick,
  onSettingsClick,
  notifications = [],
  breadcrumbs = [],
  title = 'Gestão Tributária',
  logo,
  className,
  sx,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);
  const [expandedItems, setExpandedItems] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);
  
  const handleDrawerToggle = () => {
    setOpen(!open);
  };
  
  const handleMenuExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };
  
  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };
  
  const renderMenuItem = (item, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.id];
    const isActive = item.active || (item.children && item.children.some(child => child.active));
    
    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding>
          <StyledListItemButton
            active={isActive}
            level={level}
            onClick={() => {
              if (hasChildren) {
                handleMenuExpand(item.id);
              } else if (item.onClick) {
                item.onClick();
              }
            }}
          >
            {item.icon && (
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
            )}
            
            {open && (
              <>
                <ListItemText primary={item.label} />
                {hasChildren && (
                  isExpanded ? <ExpandLess /> : <ExpandMore />
                )}
              </>
            )}
          </StyledListItemButton>
        </ListItem>
        
        {hasChildren && open && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map(child => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };
  
  const renderBreadcrumbs = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;
    
    return (
      <BreadcrumbContainer>
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{
            '& .MuiBreadcrumbs-separator': {
              color: brandColors.neutral[400],
            },
          }}
        >
          <Link
            href="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: brandColors.neutral[600],
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontFamily: designTokens.typography.fontFamily,
              '&:hover': {
                color: brandColors.primary[600],
              },
            }}
          >
            <Home sx={{ mr: 0.5, fontSize: '1rem' }} />
            Início
          </Link>
          
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            
            if (isLast) {
              return (
                <Typography
                  key={index}
                  sx={{
                    color: brandColors.neutral[800],
                    fontSize: '0.875rem',
                    fontFamily: designTokens.typography.fontFamily,
                    fontWeight: designTokens.typography.fontWeights.medium,
                  }}
                >
                  {crumb.label}
                </Typography>
              );
            }
            
            return (
              <Link
                key={index}
                href={crumb.href}
                sx={{
                  color: brandColors.neutral[600],
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontFamily: designTokens.typography.fontFamily,
                  '&:hover': {
                    color: brandColors.primary[600],
                  },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      </BreadcrumbContainer>
    );
  };
  
  return (
    <Box className={`modern-layout ${className || ''}`} sx={{ display: 'flex', ...sx }}>
      {/* App Bar */}
      <StyledAppBar
        position="fixed"
        open={open && !isMobile}
        drawerWidth={DRAWER_WIDTH}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              color: brandColors.neutral[600],
              '&:hover': {
                backgroundColor: brandColors.neutral[100],
              },
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              ml: 2,
              fontFamily: designTokens.typography.fontFamily,
              fontWeight: designTokens.typography.fontWeights.semibold,
              color: brandColors.neutral[800],
            }}
          >
            {title}
          </Typography>
          
          {/* Notifications */}
          <Tooltip title="Notificações">
            <IconButton
              onClick={handleNotificationOpen}
              sx={{
                color: brandColors.neutral[600],
                '&:hover': {
                  backgroundColor: brandColors.neutral[100],
                },
              }}
            >
              <Badge badgeContent={notifications.length} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>
          
          {/* User Menu */}
          <Tooltip title="Perfil">
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                ml: 1,
                '&:hover': {
                  backgroundColor: brandColors.neutral[100],
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: brandColors.primary[500],
                  fontSize: '0.875rem',
                  fontFamily: designTokens.typography.fontFamily,
                }}
              >
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </StyledAppBar>
      
      {/* Drawer */}
      <StyledDrawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={open}
        onClose={isMobile ? handleDrawerToggle : undefined}
        drawerWidth={DRAWER_WIDTH}
        collapsedWidth={DRAWER_WIDTH_COLLAPSED}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
      >
        <DrawerHeader>
          {open && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {logo || (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: brandColors.primary[500],
                    borderRadius: designTokens.borderRadius.md,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontWeight: designTokens.typography.fontWeights.bold,
                      fontSize: '1rem',
                    }}
                  >
                    GT
                  </Typography>
                </Box>
              )}
              <Typography
                sx={{
                  fontFamily: designTokens.typography.fontFamily,
                  fontWeight: designTokens.typography.fontWeights.semibold,
                  fontSize: '1.125rem',
                }}
              >
                {title}
              </Typography>
            </Box>
          )}
          
          {!isMobile && (
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                color: brandColors.neutral[300],
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: brandColors.neutral[800],
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
          )}
        </DrawerHeader>
        
        <List sx={{ pt: 2 }}>
          {menuItems.map(item => renderMenuItem(item))}
        </List>
      </StyledDrawer>
      
      {/* Main Content */}
      <MainContent
        open={open && !isMobile}
        drawerWidth={DRAWER_WIDTH}
        collapsedWidth={DRAWER_WIDTH_COLLAPSED}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        
        {renderBreadcrumbs()}
        
        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
      
      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            borderRadius: designTokens.borderRadius.md,
            boxShadow: designTokens.shadows.lg,
            border: `1px solid ${brandColors.neutral[200]}`,
            minWidth: 200,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {user && (
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${brandColors.neutral[200]}` }}>
            <Typography
              sx={{
                fontFamily: designTokens.typography.fontFamily,
                fontWeight: designTokens.typography.fontWeights.medium,
                fontSize: '0.875rem',
                color: brandColors.neutral[800],
              }}
            >
              {user.name || user.email}
            </Typography>
            {user.role && (
              <Typography
                sx={{
                  fontFamily: designTokens.typography.fontFamily,
                  fontSize: '0.75rem',
                  color: brandColors.neutral[600],
                }}
              >
                {user.role}
              </Typography>
            )}
          </Box>
        )}
        
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            if (onProfileClick) onProfileClick();
          }}
          sx={{
            fontSize: '0.875rem',
            fontFamily: designTokens.typography.fontFamily,
          }}
        >
          <Person sx={{ mr: 1, fontSize: '1rem' }} />
          Meu Perfil
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            if (onSettingsClick) onSettingsClick();
          }}
          sx={{
            fontSize: '0.875rem',
            fontFamily: designTokens.typography.fontFamily,
          }}
        >
          <Settings sx={{ mr: 1, fontSize: '1rem' }} />
          Configurações
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            if (onLogout) onLogout();
          }}
          sx={{
            fontSize: '0.875rem',
            fontFamily: designTokens.typography.fontFamily,
            color: brandColors.error[600],
            '&:hover': {
              backgroundColor: brandColors.error[50],
            },
          }}
        >
          <Logout sx={{ mr: 1, fontSize: '1rem' }} />
          Sair
        </MenuItem>
      </Menu>
      
      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: {
            borderRadius: designTokens.borderRadius.md,
            boxShadow: designTokens.shadows.lg,
            border: `1px solid ${brandColors.neutral[200]}`,
            minWidth: 300,
            maxWidth: 400,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${brandColors.neutral[200]}` }}>
          <Typography
            sx={{
              fontFamily: designTokens.typography.fontFamily,
              fontWeight: designTokens.typography.fontWeights.semibold,
              fontSize: '0.875rem',
              color: brandColors.neutral[800],
            }}
          >
            Notificações
          </Typography>
        </Box>
        
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: designTokens.typography.fontFamily,
                fontSize: '0.875rem',
                color: brandColors.neutral[500],
              }}
            >
              Nenhuma notificação
            </Typography>
          </Box>
        ) : (
          notifications.slice(0, 5).map((notification, index) => (
            <MenuItem
              key={index}
              onClick={handleNotificationClose}
              sx={{
                fontSize: '0.875rem',
                fontFamily: designTokens.typography.fontFamily,
                whiteSpace: 'normal',
                maxWidth: '100%',
                py: 1.5,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: designTokens.typography.fontWeights.medium,
                    color: brandColors.neutral[800],
                    mb: 0.5,
                  }}
                >
                  {notification.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: brandColors.neutral[600],
                  }}
                >
                  {notification.message}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};

ModernLayout.displayName = 'ModernLayout';

export default ModernLayout;